'use client'

/**
 * AiLabel — Kennzeichnung fuer KI-generierte / KI-modifizierte Inhalte.
 *
 * Rechtsgrundlage: EU AI Act (Regulation (EU) 2024/1689) Art. 50, in Kraft
 * seit 2. August 2026. Fuer AI-generated + Deepfake-Inhalte + KI-Chatbots
 * ist eine Kennzeichnung "in klarer und eindeutiger Weise" verpflichtend.
 *
 * Nutzt die offiziellen EU-Icons vom European Commission AI Office (Stand
 * 10. Juni 2026, frei nutzbar ohne Attribution laut EU-Portal).
 * Quelle: digital-strategy.ec.europa.eu/en/policies/eu-icons-labelling-ai-generated-content
 *
 * Zuordnung im FGL-Kontext:
 *  - "generated" — rein KI-generierte Texte OHNE redaktionelle Bearbeitung:
 *    Cirra-/Freeglidy-Antworten, KI-Fluganalyse, KI-Modell-Beratung,
 *    Wetter-Report, automatische Teilnehmer-Bescheinigung PDF
 *  - "modified"  — von Menschen erstellte Inhalte mit KI-Unterstuetzung
 *    (uebersetzung/Umformulierung), aber redaktionell durchgesehen:
 *    News-Artikel (Stephan redigiert), Feature-Beschreibungen
 *  - "basic"     — neutrale KI-Kennzeichnung ohne Grad-Angabe
 *
 * WICHTIG: FGL nutzt AUSSCHLIESSLICH selbst gehostete KI-Modelle
 * (Ollama-Cluster auf FGL-Server, RTX 5090 primary + Hetzner-Fallback).
 * KEINE externe API (OpenAI/Anthropic-API/etc.) wird zur Laufzeit fuer
 * Nutzer-Inhalte verwendet. Nutzer-Eingaben verlassen die FGL-Infrastruktur
 * nicht — das ist Kernbestandteil des Datenschutz-Konzepts. Der Badge-
 * Tooltip macht das explizit sichtbar.
 *
 * r19.55.217 (2026-08-13).
 */

import { useTranslation } from '@/i18n/client'

export type AiLabelKind = 'generated' | 'modified' | 'basic'
export type AiLabelSize = 'sm' | 'md' | 'lg'

interface AiLabelProps {
  kind: AiLabelKind
  size?: AiLabelSize
  /** Farbvariante — default 'auto' passt sich an das aktive Theme an. */
  variant?: 'black' | 'white' | 'auto'
  /** Optionaler Klassenname fuer Layout-Wrapper. */
  className?: string
  /** Kompakt = nur Icon + Screen-Reader-Text, kein sichtbares Label. */
  iconOnly?: boolean
}

const ICON_MAP: Record<AiLabelKind, { black: string; white: string }> = {
  generated: {
    black: '/icons/eu-ai/ai-generated-black.svg',
    white: '/icons/eu-ai/ai-generated-white.svg',
  },
  modified: {
    black: '/icons/eu-ai/ai-modified-black.svg',
    white: '/icons/eu-ai/ai-modified-white.svg',
  },
  basic: {
    black: '/icons/eu-ai/ai-basic-black.svg',
    white: '/icons/eu-ai/ai-basic-white.svg',
  },
}

const SIZE_MAP: Record<AiLabelSize, { icon: number; text: string; gap: string; pad: string }> = {
  sm: { icon: 14, text: 'text-[10px]', gap: 'gap-1', pad: 'px-1.5 py-0.5' },
  md: { icon: 18, text: 'text-[11px]', gap: 'gap-1.5', pad: 'px-2 py-1' },
  lg: { icon: 26, text: 'text-[13px]', gap: 'gap-2', pad: 'px-3 py-1.5' },
}

export function AiLabel({
  kind,
  size = 'md',
  variant = 'auto',
  className = '',
  iconOnly = false,
}: AiLabelProps) {
  const { t } = useTranslation()
  const sz = SIZE_MAP[size]
  const label = t(`ai.label.${kind}`)
  const tip = t(`ai.tooltip.${kind}`)
  const onPremiseNote = t('ai.onPremiseShort')

  // Auto-Variante nutzt beide, sichtbar wird jeweils nur eine (via CSS filter
  // / Theme-Class). Fuer den einfachen Fall: default = black (heller
  // Hintergrund), dark-Themes koennen die white-Variante ueber CSS-Selector
  // ansprechen. Simplify: nur eine SVG-Variante pro Aufruf.
  const iconVariant: 'black' | 'white' = variant === 'white' ? 'white' : 'black'
  const iconSrc = ICON_MAP[kind][iconVariant]

  const fullTip = `${tip} · ${onPremiseNote}`

  return (
    <span
      className={`inline-flex items-center ${sz.gap} ${sz.pad} rounded-full font-bold uppercase tracking-wider ${sz.text} ${className}`}
      style={{
        background: 'color-mix(in oklch, var(--accent, #f2c94e) 12%, transparent)',
        border: '1px solid color-mix(in oklch, var(--accent, #f2c94e) 30%, transparent)',
        color: 'var(--muted, #6b7280)',
      }}
      data-tip={fullTip}
      role="note"
      aria-label={`${label} — ${onPremiseNote}`}
    >
      <img
        src={iconSrc}
        alt=""
        width={sz.icon}
        height={sz.icon}
        aria-hidden="true"
        style={{ display: 'block', flexShrink: 0 }}
      />
      {!iconOnly && <span>{label}</span>}
    </span>
  )
}

/**
 * AiOriginNotice — laengere Text-Zeile fuer Report-Footer / Modal-Header /
 * PDF-artige Kontexte. Nennt on-premise-Hosting explizit.
 */
export function AiOriginNotice({ kind }: { kind: AiLabelKind }) {
  const { t } = useTranslation()
  const label = t(`ai.label.${kind}`)
  const long = t('ai.onPremiseLong')
  return (
    <div
      className="flex items-start gap-2 text-[11px] leading-relaxed"
      style={{ color: 'var(--muted, #6b7280)' }}
      role="note"
    >
      <img
        src={ICON_MAP[kind].black}
        alt=""
        width={22}
        height={22}
        aria-hidden="true"
        style={{ flexShrink: 0, marginTop: 1 }}
      />
      <span>
        <strong style={{ color: 'var(--text, #0a0a0a)' }}>{label}.</strong> {long}
      </span>
    </div>
  )
}
